import type { Editor } from '@tiptap/core';

/**
 * Regex to match double-dollar inline math: $$…$$
 */
export const DOUBLE_DOLLAR_INLINE_REGEX = /\$\$([^$]*?)\$\$/gi;

/**
 * Custom migration function for double-dollar inline math syntax.
 * Based on TipTap's createMathMigrateTransaction but slices 2 characters instead of 1.
 *
 * This utility converts legacy `$$…$$` inline math strings into proper TipTap math nodes.
 *
 * Original Tiptap function: https://github.com/ueberdosis/tiptap/blob/837524183bf58b87abb4b6524e1af09531de01b1/packages/extension-mathematics/src/utils.ts#L35
 *
 * @param editor - The TipTap editor instance
 * @param regex - Optional custom regex pattern (defaults to DOUBLE_DOLLAR_INLINE_REGEX)
 *
 * @example
 * ```typescript
 * import { migrateDoubleDollarMath } from './migrate-double-dollar-math'
 *
 * const editor = new Editor({
 *   onCreate: ({ editor: currentEditor }) => {
 *     provider.on('synced', () => {
 *       migrateDoubleDollarMath(currentEditor)
 *     })
 *   },
 * })
 * ```
 */
export function migrateDoubleDollarMath(editor: Editor, regex: RegExp = DOUBLE_DOLLAR_INLINE_REGEX) {
  const tr = editor.state.tr;

  tr.doc.descendants((node, pos) => {
    if (!node.isText || !node.text || !node.text.includes('$')) {
      return;
    }

    const { text } = node;

    // Use matchAll to get all matches with their indices
    const matches = Array.from(text.matchAll(regex));

    if (matches.length === 0) {
      return;
    }

    matches.forEach((match) => {
      const mathMatch = match[0];
      const start = match.index!;
      const end = start + mathMatch.length;
      const from = tr.mapping.map(pos + start);
      const $from = tr.doc.resolve(from);
      const parent = $from.parent;
      const index = $from.index();
      const { inlineMath } = editor.schema.nodes;

      if (!parent.canReplaceWith(index, index + 1, inlineMath)) {
        return;
      }

      // Slice off 2 characters on each side for $$…$$
      tr.replaceWith(
        tr.mapping.map(pos + start),
        tr.mapping.map(pos + end),
        inlineMath.create({ latex: mathMatch.slice(2, -2) }),
      );
    });
  });

  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
}
